import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { getApiBaseUrl } from '../utils/apiBaseUrl';
import './VehicleUrgent.css';

function getAuthToken() {
  try {
    const stored = JSON.parse(localStorage.getItem('user') || '{}');
    return stored?.token ? String(stored.token) : '';
  } catch {
    return '';
  }
}

function authHdr() {
  const t = getAuthToken();
  return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) };
}

function ddayLabel(days) {
  if (days == null) return '';
  if (days < 0) return `만료 ${Math.abs(days)}일 지남`;
  if (days === 0) return 'D-Day';
  return `D-${days}`;
}

function ddayClass(days) {
  if (days == null) return '';
  if (days < 0 || days <= 7) return 'vehicle-urgent-item--danger';
  return 'vehicle-urgent-item--warning';
}

async function fetchUrgentItems() {
  const base = getApiBaseUrl();
  const opts = { credentials: 'include', headers: authHdr() };
  const [vehicleRes, equipmentRes] = await Promise.all([
    fetch(`${base}/api/erp_corporate_vehicle/urgent-checks?_=${Date.now()}`, opts).then((r) => (r.ok ? r.json() : null)).catch(() => null),
    fetch(`${base}/api/erp_equipment/urgent-checks?_=${Date.now()}`, opts).then((r) => (r.ok ? r.json() : null)).catch(() => null),
  ]);
  const vehicleItems = (vehicleRes?.ok && Array.isArray(vehicleRes.items) ? vehicleRes.items : []).map((item) => ({
    ...item,
    kind: 'vehicle',
    key: `v-${item.vehicle_seq}-${item.check_type}-${item.expire_date}`,
  }));
  const equipmentItems = (equipmentRes?.ok && Array.isArray(equipmentRes.items) ? equipmentRes.items : []).map((item) => ({
    ...item,
    kind: 'equipment',
    key: `e-${item.equipment_seq}-${item.expire_date}`,
  }));
  return [...vehicleItems, ...equipmentItems].sort((a, b) => (
    (Number(a.dday ?? 9999) - Number(b.dday ?? 9999))
    || String(a.kind).localeCompare(String(b.kind))
  ));
}

function UrgentListModal({ items, onClose, onSelectItem }) {
  return createPortal(
    <div className="vehicle-urgent-modal" onClick={onClose} role="presentation">
      <div
        className="vehicle-urgent-modal__panel vehicle-urgent-modal__panel--list"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="vehicle-urgent-list-title"
      >
        <div className="vehicle-urgent-modal__head">
          <span className="header-urgent-badge header-urgent-badge--static">긴급</span>
          <h3 id="vehicle-urgent-list-title" className="vehicle-urgent-modal__title">긴급 체크사항</h3>
        </div>
        <p className="vehicle-urgent-modal__sub">
          차량 보험·검사 30일 이내, 장비 교정 60일 이내 — 항목을 누르면 상세를 볼 수 있습니다.
        </p>
        {items.length === 0 ? (
          <div className="vehicle-urgent-modal__empty">표시할 긴급 항목이 없습니다.</div>
        ) : (
          <ul className="vehicle-urgent-list">
            {items.map((item) => {
              const isVehicle = item.kind === 'vehicle';
              const title = isVehicle
                ? (item.vehicle_name || item.plate_number || '법인차량')
                : (item.equipment_name || item.model_name || '장비');
              const badge = isVehicle
                ? item.plate_number
                : (item.management_no || item.category || '장비');
              return (
                <li key={item.key}>
                  <button
                    type="button"
                    className={`vehicle-urgent-item ${ddayClass(item.dday)}`}
                    onClick={() => onSelectItem(item)}
                  >
                    <span className="vehicle-urgent-item__type">{item.check_type}</span>
                    <span className="vehicle-urgent-item__plate">{badge}</span>
                    <span className="vehicle-urgent-item__name">{title}</span>
                    <span className="vehicle-urgent-item__dday">{ddayLabel(item.dday)}</span>
                    <span className="vehicle-urgent-item__date">{item.expire_date}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        <button type="button" className="vehicle-urgent-modal__close vehicle-urgent-modal__close--secondary" onClick={onClose}>
          닫기
        </button>
      </div>
    </div>,
    document.body,
  );
}

function UrgentDetailModal({ item, onClose, onBack }) {
  if (!item) return null;

  const isVehicle = item.kind === 'vehicle';
  const title = isVehicle
    ? (item.vehicle_name || item.plate_number || '법인차량')
    : (item.equipment_name || item.model_name || '장비');
  const contactBits = isVehicle
    ? [
      item.check_type === '보험' ? item.insurance_company : '',
      item.check_type === '보험' ? item.insurance_contact : '',
    ].filter(Boolean)
    : [item.usage_location].filter(Boolean);

  return createPortal(
    <div className="vehicle-urgent-modal" onClick={onClose} role="presentation">
      <div
        className="vehicle-urgent-modal__panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="vehicle-urgent-detail-title"
      >
        <div className="vehicle-urgent-modal__head">
          <span className="header-urgent-badge header-urgent-badge--static">긴급</span>
          <h3 id="vehicle-urgent-detail-title" className="vehicle-urgent-modal__title">
            {item.check_type} 만료 임박
          </h3>
        </div>
        <div className="vehicle-urgent-modal__body">
          {isVehicle ? (
            <>
              <div className="vehicle-urgent-modal__row">
                <span className="vehicle-urgent-modal__label">차량번호</span>
                <span className="vehicle-urgent-modal__value">{item.plate_number || '—'}</span>
              </div>
              <div className="vehicle-urgent-modal__row">
                <span className="vehicle-urgent-modal__label">차량명</span>
                <span className="vehicle-urgent-modal__value">{title}</span>
              </div>
            </>
          ) : (
            <>
              <div className="vehicle-urgent-modal__row">
                <span className="vehicle-urgent-modal__label">관리번호</span>
                <span className="vehicle-urgent-modal__value">{item.management_no || '—'}</span>
              </div>
              <div className="vehicle-urgent-modal__row">
                <span className="vehicle-urgent-modal__label">품명</span>
                <span className="vehicle-urgent-modal__value">{title}</span>
              </div>
              <div className="vehicle-urgent-modal__row">
                <span className="vehicle-urgent-modal__label">분류</span>
                <span className="vehicle-urgent-modal__value">{item.category || '—'}</span>
              </div>
            </>
          )}
          <div className="vehicle-urgent-modal__row">
            <span className="vehicle-urgent-modal__label">구분</span>
            <span className="vehicle-urgent-modal__value">{item.check_type}</span>
          </div>
          <div className="vehicle-urgent-modal__row">
            <span className="vehicle-urgent-modal__label">만료일</span>
            <span className="vehicle-urgent-modal__value">{item.expire_date || '—'}</span>
          </div>
          <div className="vehicle-urgent-modal__row">
            <span className="vehicle-urgent-modal__label">남은 일수</span>
            <span className={`vehicle-urgent-modal__value vehicle-urgent-modal__dday ${ddayClass(item.dday)}`}>
              {ddayLabel(item.dday)}
            </span>
          </div>
          {contactBits.length > 0 ? (
            <div className="vehicle-urgent-modal__row">
              <span className="vehicle-urgent-modal__label">{isVehicle ? '연락처' : '사용처'}</span>
              <span className="vehicle-urgent-modal__value">{contactBits.join(' · ')}</span>
            </div>
          ) : null}
          <p className="vehicle-urgent-modal__hint">
            {isVehicle
              ? '보험·검사 갱신 여부를 확인하고 필요 시 ERP 법인차량 관리 메뉴에서 처리해 주세요.'
              : '교정 일정을 확인하고 필요 시 ERP 장비관리 메뉴에서 처리해 주세요.'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, padding: '0 18px 18px' }}>
          {onBack ? (
            <button type="button" className="vehicle-urgent-modal__close vehicle-urgent-modal__close--secondary" style={{ margin: 0, flex: 1 }} onClick={onBack}>
              목록
            </button>
          ) : null}
          <button type="button" className="vehicle-urgent-modal__close" style={{ margin: 0, flex: 1 }} onClick={onClose}>
            확인
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default function VehicleUrgentHeaderBadge() {
  const [items, setItems] = useState([]);
  const [listOpen, setListOpen] = useState(false);
  const [detailItem, setDetailItem] = useState(null);

  useEffect(() => {
    const token = getAuthToken();
    if (!token) {
      setItems([]);
      return undefined;
    }
    let cancelled = false;
    fetchUrgentItems()
      .then((list) => { if (!cancelled) setItems(list); })
      .catch(() => { if (!cancelled) setItems([]); });
    return () => { cancelled = true; };
  }, []);

  const countLabel = useMemo(() => (items.length > 0 ? ` ${items.length}` : ''), [items.length]);

  if (!items.length) return null;

  const closeAll = () => {
    setListOpen(false);
    setDetailItem(null);
  };

  return (
    <>
      <button
        type="button"
        className="header-urgent-badge"
        onClick={() => { setDetailItem(null); setListOpen(true); }}
        title={`긴급 체크사항${countLabel}건`}
        aria-label={`긴급 체크사항${countLabel}건`}
      >
        긴급{countLabel}
      </button>

      {listOpen && !detailItem ? (
        <UrgentListModal
          items={items}
          onClose={closeAll}
          onSelectItem={(item) => setDetailItem(item)}
        />
      ) : null}

      {detailItem ? (
        <UrgentDetailModal
          item={detailItem}
          onClose={closeAll}
          onBack={listOpen ? () => setDetailItem(null) : null}
        />
      ) : null}
    </>
  );
}
